import { useRef } from "react";
import { Alert, Button, Card, Empty, Space, Spin, Tag, Tooltip, Typography, Upload } from "antd";
import { DeleteOutlined, FileWordOutlined, InboxOutlined } from "@ant-design/icons";

import styles from "./DocumentBuilder.module.css";

const { Dragger } = Upload;
const { Text } = Typography;

const formatSize = (bytes) => {
  if (!bytes) return "";
  const megabytes = bytes / (1024 * 1024);
  return megabytes >= 1 ? `${megabytes.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

/**
 * Left panel: the documents the new document is assembled from. Dropping a
 * file here reads its structure straight away, so the middle panel can offer
 * its sections without a separate step.
 */
const SourcePanel = ({ documents, analyzing, maxDocuments, onAdd, onRemove }) => {
  const atCapacity = documents.length >= maxDocuments;
  // Ant Design calls beforeUpload once per file with the whole batch as the
  // second argument. Track the batch so we only forward it once.
  const batchRef = useRef(null);

  const takeBatch = (fileList) => {
    if (batchRef.current === fileList) return;
    batchRef.current = fileList;
    const files = fileList.map((item) => item.originFileObj || item).filter((item) => item && item.name);
    if (files.length) onAdd(files);
  };

  return (
    <Card
      size="small"
      title="Source documents"
      className={styles.panel}
      styles={{ body: { display: "flex", flexDirection: "column", gap: 12, overflow: "hidden", flex: 1 } }}
      extra={documents.length ? <Text type="secondary">{documents.length}</Text> : null}
    >
      <Dragger
        multiple
        accept=".docx"
        showUploadList={false}
        disabled={analyzing || atCapacity}
        // Stay empty: the panel owns the real list, so a removed file cannot
        // reappear the next time something is dropped.
        fileList={[]}
        beforeUpload={(_file, fileList) => {
          takeBatch(fileList);
          // LIST_IGNORE keeps the file out of Upload's internal list. Returning
          // false would still accumulate it, which is how deleted files came back.
          return Upload.LIST_IGNORE;
        }}
        className={styles.dragger}
      >
        <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
          <InboxOutlined />
        </p>
        <p className="ant-upload-text" style={{ fontSize: 13 }}>
          Drop Word documents here
        </p>
        <p className="ant-upload-hint" style={{ fontSize: 11 }}>
          .docx files, up to {maxDocuments} at a time
        </p>
      </Dragger>

      {atCapacity && <Alert type="info" showIcon message={`Holding the maximum of ${maxDocuments} documents.`} />}

      <div className={styles.scrollArea}>
        {analyzing && (
          <div className={styles.centered}>
            <Space direction="vertical" align="center" size={10}>
              <Spin size="small" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Reading document structure...
              </Text>
            </Space>
          </div>
        )}

        {!analyzing && documents.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary" style={{ fontSize: 12 }}>No documents yet</Text>}
          />
        )}

        {documents.map((document) => (
          <div key={document.documentId} className={styles.sourceCard}>
            <FileWordOutlined className={styles.sourceIcon} />
            <div className={styles.sourceDetails}>
              <Tooltip title={document.filename}>
                <Text ellipsis className={styles.sourceName}>
                  {document.filename}
                </Text>
              </Tooltip>
              <div className={styles.sourceMeta}>
                <Tag color="blue">{document.outline.headingCount} sections</Tag>
                {document.outline.contentControlCount > 0 && (
                  <Tag color="magenta">{document.outline.contentControlCount} content controls</Tag>
                )}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {formatSize(document.size)}
                </Text>
              </div>
            </div>
            <Tooltip title="Remove">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onRemove(document.documentId)}
              />
            </Tooltip>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default SourcePanel;
